import { TranslationKey, useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { Children, cloneElement, isValidElement, ReactNode } from 'react';
import { ICurrentWorkspaceStore } from '../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';

interface IProps {
  disabledPropsName?: string;
  disabled?: boolean;
  children: ReactNode;
  limitType: 'workspace' | 'user' | 'space' | 'any';
  isExceeded?: boolean;
}

export const DisabledWhenPolicyExceeded = observer((props: IProps): ReactNode => {
  const {
    currentWorkspace: { encryption, ownerPlanInfo },
  } = useInjection(ICurrentWorkspaceStore.$);

  const { limitType, isExceeded } = props;
  const { t } = useTranslator();
  let isLimitExceeded = false;
  let title: TranslationKey = 'policy_limit_exceeded_block';

  if (isExceeded !== undefined) {
    isLimitExceeded = isExceeded;
  } else if (ownerPlanInfo) {
    switch (limitType) {
      case 'workspace':
        isLimitExceeded = ownerPlanInfo.usedLimits.workspaceLimitReached;
        title = 'workspace_limit_exceeded_block';
        break;
      case 'user':
        isLimitExceeded = ownerPlanInfo.usedLimits.workspaceUsersLimitReached;
        title = 'user_limit_exceeded_block';
        break;
      case 'space':
        isLimitExceeded = ownerPlanInfo.usedLimits.availableSpaceLimitReached;
        title = 'space_limit_exceeded_block';
        break;
      case 'any':
        isLimitExceeded =
          ownerPlanInfo.usedLimits.workspaceLimitReached ||
          ownerPlanInfo.usedLimits.workspaceUsersLimitReached ||
          ownerPlanInfo.usedLimits.availableSpaceLimitReached;
    }
  }

  const isEncryptionDisabled = encryption.encrypted && !encryption.encryptionAllowed;
  const disabledPropsName: string = props.disabledPropsName ?? 'disabled';

  const getTitle = (childTitle: string): string => {
    if (isEncryptionDisabled) {
      return t('encryption_unavailable_block');
    }
    if (isLimitExceeded) {
      return title ?? t('policy_limit_exceeded_block');
    }
    return childTitle;
  };

  return Children.map(props.children, (child): ReactNode => {
    if (isValidElement<{ title: string; disabled: boolean }>(child)) {
      const childProps: Record<string, string | boolean | undefined> = {
        title: getTitle(child.props.title),
      };
      const childDisabled = child.props.disabled;
      childProps[disabledPropsName] = childDisabled || isLimitExceeded || isEncryptionDisabled || props.disabled;
      return cloneElement(child, childProps);
    }
    return child;
  });
});
